import React from 'react';
import {useTranslation} from 'react-i18next';
import {Info} from '../../icons';
import {
    clientQueryCount,
    relativeTimeLabel,
} from '../../portal_usage/portalUsageState';

/**
 * Los cuatro números de arriba de la sección: cuánto, cada cuánto, qué abrió y cuándo fue la
 * última vez.
 *
 * "Cada cuánto" (días con actividad) va al mismo nivel que "cuánto" a propósito: con decenas de
 * consultas al mes, el total solo no distingue al cliente que entró nueve veces en el mes del que
 * hizo todo en una sola tarde y no volvió — y esa diferencia es la que decide si hay que llamarlo.
 */
export function PortalUsageSummaryStrip({usage}) {
    const {t} = useTranslation();

    const client = (usage.assistant && usage.assistant.client) || {};
    const portalWeb = usage.portal_web || {};
    const windowDays = usage.window_days || 30;
    const lastLabel = relativeTimeLabel(usage.last_activity_at, t);
    const lastUser = mostRecentPortalUser(usage.portal_users);
    const queries = clientQueryCount(usage);

    return (
        <div className="pu-kpis">
            <Tile
                label={t('portal_usage.kpi_client_queries', 'Consultas del cliente')}
                value={queries}
                tone={queries ? 'ia' : 'none'}
                sub={queriesSub(usage, t)}
                help={t('portal_usage.kpi_queries_help',
                    'Una <strong>consulta</strong> es una pregunta o un pedido de datos de tu cliente. '
                    + 'No cuenta lo que su asistente revisa por su cuenta para armar la respuesta, como '
                    + 'ver qué cliente es o qué métricas hay.')}
            />
            <Tile
                label={t('portal_usage.kpi_active_days', 'Días con actividad')}
                value={client.active_days || 0}
                tone={client.active_days ? null : 'none'}
                sub={t('portal_usage.kpi_active_days_sub', 'de {{days}}', {days: windowDays})}
            />
            <Tile
                label={t('portal_usage.kpi_portal_views', 'Ingresos al portal')}
                value={portalWeb.views || 0}
                tone={portalWeb.views ? null : 'none'}
                sub={portalWeb.active_days
                    ? t('portal_usage.kpi_portal_views_sub', 'en {{days}} días distintos', {days: portalWeb.active_days})
                    : t('portal_usage.kpi_portal_views_none', 'reportes, tablero y memoria')}
            />
            <Tile
                label={t('portal_usage.kpi_last_activity', 'Última actividad')}
                // Sin actividad NUNCA se muestra un cero ni una fecha inventada: un guion dice
                // "no pasó", que es distinto de "pasó y valió cero".
                value={lastLabel || '—'}
                tone={lastLabel ? 'ok' : 'none'}
                sub={lastUser ? (lastUser.name || lastUser.email) : null}
            />
        </div>
    );
}

/** El usuario del portal que estuvo activo más recientemente (los que nunca entraron van último). */
export function mostRecentPortalUser(users) {
    const list = (users || []).filter(user => user && user.last_at);
    if (!list.length) return null;
    return list.reduce((best, user) => (
        new Date(user.last_at) > new Date(best.last_at) ? user : best
    ), list[0]);
}

/**
 * El sub del primer tile: los errores de consulta.
 *
 * Sin «+N pasos del asistente» (card k14eI6RC, call con Leo del 07/10): nadie entendía qué eran
 * —«no me queda claro qué son pasos»— y no dicen nada de lo que el cliente pidió. Siguen adentro de
 * cada consulta desplegada (`PortalUsageTurn`), donde se ve qué hizo el asistente para responderla.
 *
 * Los errores son los de CONSULTA (`query_errors`), no los de cualquier fila: el tile de arriba
 * cuenta consultas, así que un contador de errores sobre todas las interacciones podía dar más
 * errores que consultas en la misma caja.
 */
function queriesSub(usage, t) {
    const client = (usage.assistant && usage.assistant.client) || {};
    const errors = typeof client.query_errors === 'number' ? client.query_errors : client.errors;
    if (errors) {
        return errors === 1
            ? t('portal_usage.kpi_client_queries_errors_one', '{{n}} no devolvió datos', {n: errors})
            : t('portal_usage.kpi_client_queries_errors', '{{n}} no devolvieron datos', {n: errors});
    }
    return t('portal_usage.kpi_client_queries_sub', 'desde el asistente y la API');
}

function Tile({label, value, sub, tone, help}) {
    return (
        <div className="pu-kpi">
            <div className="pu-kpi__lbl">
                {label}
                {/* Es el copy más importante del cambio: es lo que impide que un número que BAJA
                    (porque antes contaba llamadas) se lea como una regresión. HTML estático de la
                    key, sin interpolar nada de terceros. */}
                {help ? (
                    <span className="info-icon-container pu-kpi__help">
                        <Info size={13}/>
                        <div className="warning-tooltip -below -rich">
                            <div className="warning-tooltip-body">
                                <p className="warning-tooltip-text" dangerouslySetInnerHTML={{__html: help}}/>
                            </div>
                        </div>
                    </span>
                ) : null}
            </div>
            <div className={`pu-kpi__val${tone ? ` -${tone}` : ''}`}>{value}</div>
            {sub ? <div className="pu-kpi__sub">{sub}</div> : null}
        </div>
    );
}

export default PortalUsageSummaryStrip;
